/**
 * Canonical permission taxonomy for the client.
 * Mirrors `IPermissions` in server/src/models/rolePermissions.ts exactly.
 *
 * This is intentionally the one client-side source of truth for the shape —
 * client/src/utils/defaultPermissions.ts and server/src/models/permissionSet.ts
 * define an older, divergent shape and should not be used for new work.
 */

export interface PermissionsShape {
  dashboard: boolean;
  notifications: boolean;
  userProfile: {
    update_profile: boolean;
    view_profile: boolean;
    update_personal_info: boolean;
    update_emergency_contact: boolean;
    update_employment_detail: boolean;
  };
  dashboardAccess: {
    view_department_dashboard: boolean;
    view_supervisor_dashboard: boolean;
  };
  holidays: boolean;
  holiday_managment: {
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  attendance: {
    admin: boolean;
    employee: boolean;
    viewAll: boolean;
    viewSelf: boolean;
  };
  employees: {
    allEmployees: boolean;
    department: boolean;
    leaveEmployees: boolean;
    adminLeave: boolean;
    leaveConfiguration: boolean;
    viewProfile: boolean;
    addleave: boolean;
    employeeleaveEdit: boolean;
    adminleaveReject: boolean;
    adminLeaveApproved: boolean;
    userBalance: boolean;
    balanceSummaryDownload: boolean;
  };
  reports: {
    leaveReports: boolean;
    attendanceReports: boolean;
    leaveHistoryReport: boolean;
    leaveReportDownload: boolean;
    attendancereportDownload: boolean;
    leaveBalanceReportExport: boolean;
    leaveTemplate: boolean;
    leaveImport: boolean;
    attendanceTemplate: boolean;
    attendanceImport: boolean;
    balanceTemplate: boolean;
    balanceImport: boolean;
    balanceApprove: boolean;
  };
  adminSettings: boolean;
  superAdminPanel: boolean;
  view_audit_logs: boolean;
  organization_management: {
    memberAdd: boolean;
    organizationUpdate: boolean;
  };
  user_management: {
    changePassword: boolean;
    changePin: boolean;
  };
  department: {
    view: boolean;
    add: boolean;
    update: boolean;
    delete: boolean;
  };
  permission_management: {
    view_user_permissions: boolean;
    edit_users_permissions: boolean;
  };
  role_management: {
    create_role: boolean;
  };
  ticket: {
    view: boolean;
    create: boolean;
  };
  program_management: {
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  employment_history: {
    add: boolean;
    update: boolean;
    delete: boolean;
  };
  configuration_management: {
    user_configrations_hours: boolean;
    department_configrations_hours: boolean;
    program_configrations: boolean;
  };
}

export const PERMISSIONS_SKELETON: PermissionsShape = {
  dashboard: false,
  notifications: false,
  userProfile: {
    update_profile: false,
    view_profile: false,
    update_personal_info: false,
    update_emergency_contact: false,
    update_employment_detail: false,
  },
  dashboardAccess: {
    view_department_dashboard: false,
    view_supervisor_dashboard: false,
  },
  holidays: false,
  holiday_managment: {
    create: false,
    update: false,
    delete: false,
  },
  attendance: {
    admin: false,
    employee: false,
    viewAll: false,
    viewSelf: false,
  },
  employees: {
    allEmployees: false,
    department: false,
    leaveEmployees: false,
    adminLeave: false,
    leaveConfiguration: false,
    viewProfile: false,
    addleave: false,
    employeeleaveEdit: false,
    adminleaveReject: false,
    adminLeaveApproved: false,
    userBalance: false,
    balanceSummaryDownload: false,
  },
  reports: {
    leaveReports: false,
    attendanceReports: false,
    leaveHistoryReport: false,
    leaveReportDownload: false,
    attendancereportDownload: false,
    leaveBalanceReportExport: false,
    leaveTemplate: false,
    leaveImport: false,
    attendanceTemplate: false,
    attendanceImport: false,
    balanceTemplate: false,
    balanceImport: false,
    balanceApprove: false,
  },
  adminSettings: false,
  superAdminPanel: false,
  view_audit_logs: false,
  organization_management: {
    memberAdd: false,
    organizationUpdate: false,
  },
  user_management: {
    changePassword: false,
    changePin: false,
  },
  department: {
    view: false,
    add: false,
    update: false,
    delete: false,
  },
  permission_management: {
    view_user_permissions: false,
    edit_users_permissions: false,
  },
  role_management: {
    create_role: false,
  },
  ticket: {
    view: false,
    create: false,
  },
  program_management: {
    create: false,
    update: false,
    delete: false,
  },
  employment_history: {
    add: false,
    update: false,
    delete: false,
  },
  configuration_management: {
    user_configrations_hours: false,
    department_configrations_hours: false,
    program_configrations: false,
  },
};

/**
 * Overlays whatever a member's persisted permissions doc actually has on top
 * of the full skeleton. `permissions` is stored as Mongoose `Mixed`, so an
 * older doc can be missing keys added later — those stay `false` (unchecked)
 * instead of being silently dropped from the form. Unknown/legacy keys in
 * the fetched doc (e.g. from the older `connect`/`projects` shape) are
 * ignored since only skeleton keys are read.
 */
export function mergeWithPermissionSkeleton(
  fetched: Record<string, unknown> | null | undefined
): PermissionsShape {
  const result = JSON.parse(JSON.stringify(PERMISSIONS_SKELETON)) as PermissionsShape;
  if (!fetched) return result;

  (Object.keys(result) as Array<keyof PermissionsShape>).forEach((key) => {
    const skeletonValue = result[key];
    const fetchedValue = fetched[key];

    if (typeof skeletonValue === "boolean") {
      if (typeof fetchedValue === "boolean") {
        (result[key] as boolean) = fetchedValue;
      }
    } else if (
      typeof skeletonValue === "object" &&
      skeletonValue !== null &&
      fetchedValue &&
      typeof fetchedValue === "object"
    ) {
      const fetchedSub = fetchedValue as Record<string, unknown>;
      Object.keys(skeletonValue).forEach((subKey) => {
        if (typeof fetchedSub[subKey] === "boolean") {
          (skeletonValue as Record<string, boolean>)[subKey] = fetchedSub[subKey] as boolean;
        }
      });
    }
  });

  return result;
}
